/**
 * vc-engine.js — page-side facade of on-device voice conversion (window.VoiceConvert):
 * speech or singing → the timbre of one catalogue voice, in the browser, no server.
 * The networks run in vc-worker.js; the pure recipe lives in vc-shared.js (one copy for
 * the worker, this facade and the unit tests).
 *
 * The lane (measured 18.10.2026, 12 FLEURS ru sources × the catalogue, ECAPA / UTMOS /
 * Whisper / pyin — docs/voice-gender-catalogue.md): per-voice RVC v2 (40 kHz, HiFi-GAN
 * NSF) on ContentVec features, pitch from OUR RMVPE (window.RmvpePitch — the tracker
 * /audio-to-notes already ships, not a second copy). Each model is ONE file, a voice
 * pack: ONNX bytes + the known answer that decides whether this device computes it.
 *
 * Public API:
 *   support({allowWasm})                     → Promise<{ok, reason?, providers}>  downloads nothing
 *   preload(slug, {signal, onProgress, allowWasm}) → Promise<{provider, katMs}>
 *   convert(audioBuffer, { voice:{id, url, fallbackUrl}, semitones: n | 'auto', targetF0, slug, signal,
 *                          onProgress(p, phase), allowWasm, seed })
 *       → Promise<{ audio:Float32Array, sr, provider, ms, f0Ms, semitones, sourceF0 }>
 *   estimateWorkS(seconds)                   → seconds of work on this device (after preload), or null
 *   release()                                → terminates the worker (the only way wasm memory returns)
 *
 * Wiring comes from the page (tools/partials/voice-convert-engine): the worker URL and the
 * ContentVec pack URL pair are <meta> tags, the voice pack URLs come with the catalogue,
 * the ORT build is window.__ortBuild (native WebGPU — the JSEP build computes the RVC
 * generator wrong: 21 dB against 79 dB, bench 18.10).
 */
(function () {
'use strict';

var SR = 16000;
var MIN_DEVICE_GB = 4;
// what a first conversion downloads: ContentVec 95 MB + RMVPE 91 MB + one voice 29 MB
var PACKS_MB = 215;
var MAX_VOICES_WARM = 2;            // generator sessions kept in the worker (29 MB of weights each)

function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return (m && m.content) || ''; }
function abs(u) { try { return new URL(u, window.location.href).href; } catch (_) { return u; } }
function tele(slug, action, payload) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug || 'voice-convert', action: action }, payload || {})); } catch (_) {}
}
function typed(code, message) { var e = new Error(message || code); e.code = code; return e; }
function S() { return window.VcShared; }

function loadScript(path, probe) {
    if (probe()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__jsSrc ? window.__jsSrc(path) : path;
        s.onload = function () { probe() ? resolve() : reject(typed('dep_missing', path + ' loaded without its global')); };
        s.onerror = function () { reject(typed('dep_missing', path + ' failed to load')); };
        document.head.appendChild(s);
    });
}
function needVerifier() { return loadScript('/js/ort-ep-verify.js', function () { return !!window.OrtEpVerify; }); }
function needDownloader() { return loadScript('/js/model-download.js', function () { return !!self.__mdFetchBytes; }); }

var adapterInfo = null, cacheKey = null;

function support(opts) {
    opts = opts || {};
    var allowWasm = !!opts.allowWasm;
    if (!window.Worker) return Promise.resolve({ ok: false, reason: 'no_worker' });
    if (!meta('vc-worker-url') || !meta('vc-cv-url') || !window.__ortBuild || !window.VcShared) return Promise.resolve({ ok: false, reason: 'not_wired' });
    if (!window.RmvpePitch) return Promise.resolve({ ok: false, reason: 'no_pitch' });
    // «can this device afford it» has one owner: a HARD refusal (memory, processor) ends
    // here; a soft one (connection, data saver) is the page's to show with its cost.
    var aff = window.__deviceCap && window.__deviceCap.modelAffordable
        ? window.__deviceCap.modelAffordable({ mb: PACKS_MB, minMemoryGB: MIN_DEVICE_GB }) : { ok: true };
    if (!aff.ok && !aff.soft) return Promise.resolve({ ok: false, reason: aff.reason === 'memory' ? 'low_memory' : 'device' });
    var native = typeof window.__ortBuild.nativeGpu === 'function' ? window.__ortBuild.nativeGpu() : null;
    var cpuOnly = function (reason) {
        return allowWasm ? { ok: true, providers: ['wasm'], gpuReason: reason, build: null } : { ok: false, reason: reason };
    };
    if (!native) return Promise.resolve(cpuOnly('webkit'));
    if (!navigator.gpu) return Promise.resolve(cpuOnly('no_webgpu'));
    return needVerifier().then(function () { return window.OrtEpVerify.adapterIdentity(); }).then(function (id) {
        adapterInfo = id || null;
        if (!id || !id.present) return cpuOnly('no_webgpu');
        if (id.fallback) return cpuOnly('software_gpu');
        var ua = (navigator.userAgent.match(/(?:Chrome|Firefox|Edg)\/(\d+)/) || [])[1] || '0';
        cacheKey = window.OrtEpVerify.verdictKey(['rvc', 'ort-native', id.vendor, id.architecture, 'b' + ua]);
        if (window.OrtEpVerify.isRejected(cacheKey, 'webgpu')) return cpuOnly('gpu_rejected');
        return { ok: true, providers: allowWasm ? ['webgpu', 'wasm'] : ['webgpu'], build: native, adapter: id };
    }, function () { return cpuOnly('no_webgpu'); });
}

var worker = null, ready = null, readyInfo = null, waiters = {}, seq = 0, currentSlug = null;
var voicesWarm = [];                 // ids, most recent last
var voiceLoads = {};                 // id → Promise

function rejectAll(err) {
    Object.keys(waiters).forEach(function (k) { try { waiters[k].reject(err); } catch (_) {} });
    waiters = {};
}
function release() {
    if (worker) { try { worker.terminate(); } catch (_) {} }
    worker = null; ready = null; readyInfo = null; voicesWarm = []; voiceLoads = {};
    rejectAll(typed('released', 'engine released'));
}

function onVerdict(v, provider) {
    provider = (v && v.provider) || provider;
    var flat = { ep: provider, ok: v && v.ok ? 1 : 0, snr: v && v.snr, net: v && v.net, ms: v && v.ms || null,
                 round: v && v.round || null, err: v && v.error ? String(v.error).slice(0, 80) : null, skipped: v && v.skipped || null };
    if (adapterInfo) { flat.gpu_vendor = adapterInfo.vendor || null; flat.gpu_arch = adapterInfo.architecture || null; }
    tele(currentSlug, 'vc_verdict', flat);
    // Only a WRONG ANSWER is remembered (30 days); a throw or a cancel is a bad day.
    if (v && !v.ok && !v.error && !v.skipped && provider === 'webgpu' && cacheKey && window.OrtEpVerify) {
        try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat', why: v.net || null }); } catch (_) {}
    }
}

var RUNG_DEADLINE_MS = { webgpu: 120000, wasm: 300000 };
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var t = setTimeout(fn, ms); return function () { clearTimeout(t); };
}

function wire(w) {
    w.onmessage = function (ev) {
        var m = ev.data || {};
        if (m.type === 'progress') { var p = waiters['job:' + m.job]; if (p && p.onProgress) p.onProgress(m.done, m.of); return; }
        if (m.type === 'note') { tele(currentSlug, 'vc_note', { note: m.note }); return; }
        // every known-answer round of the worker's OrtEpVerify.ladder, as it happens
        if (m.type === 'verdict') { onVerdict(Object.assign({ net: m.net }, m.verdict), readyInfo && readyInfo.provider); return; }
        var key = m.type === 'ready' ? 'ready'
                : m.type === 'voice_ready' ? 'voice:' + m.id
                : m.type === 'result' ? 'job:' + m.job
                : m.type === 'error' ? (m.job != null ? 'job:' + m.job : m.id != null ? 'voice:' + m.id : 'ready') : null;
        var h = key && waiters[key];
        if (!h) return;
        delete waiters[key];
        if (m.type === 'error') { var e = typed(m.code || 'vc_error', m.message); e.verdict = m.verdict || null; h.reject(e); }
        else h.resolve(m);
    };
    w.onerror = function (ev) { rejectAll(typed('worker_failed', (ev && ev.message) || 'worker error')); };
}
function waitFor(key, extra) {
    return new Promise(function (resolve, reject) { waiters[key] = Object.assign({ resolve: resolve, reject: reject }, extra || {}); });
}

/** One worker = one provider = one ORT instance; the ladder crosses workers (rmvpe-pitch.js, 23.09). */
function startRung(prov, build, cvBuf, last, slug) {
    var w;
    try { w = new Worker(meta('vc-worker-url')); } catch (e) { return Promise.reject(typed('worker_failed', String(e && e.message || e))); }
    worker = w; wire(w);
    var buf = last ? cvBuf : cvBuf.slice(0);
    var p = waitFor('ready');
    var cancel = visibleTimeout(function () {
        tele(slug, 'vc_rung_timeout', { ep: prov });
        var h = waiters.ready; if (h) { delete waiters.ready; h.reject(typed('rung_timeout', prov + ' did not become ready')); }
    }, RUNG_DEADLINE_MS[prov] || 120000);
    w.postMessage({ type: 'init', provider: prov, cvPack: buf,
        ortUrl: abs(build.url), wasmBase: abs(build.wasmBase),
        ortFallbackUrl: abs(build.fallbackUrl), fallbackWasmBase: abs(build.fallbackWasmBase || build.wasmBase) }, [buf]);
    return p.then(function (m) { cancel(); return { provider: prov, unverified: !!m.unverified, katMs: { cv: m.verdict && m.verdict.ms } }; },
        function (e) { cancel(); try { w.terminate(); } catch (_) {} if (worker === w) worker = null; throw e; });
}

function fetchPack(urls, signal, onProgress, slug) {
    return needDownloader().then(function () {
        var tryUrl = function (i) {
            return self.__mdFetchBytes(urls[i], {
                signal: signal,
                onProgress: function (got, total) { if (onProgress && total > 0) onProgress(Math.min(1, got / total)); }
            }).catch(function (e) {
                if (i + 1 < urls.length && !(signal && signal.aborted)) { tele(slug, 'vc_pack_fallback', { reason: String(e && e.message || e).slice(0, 80) }); return tryUrl(i + 1); }
                throw e;
            });
        };
        return tryUrl(0);
    }).then(function (bytes) {
        return bytes.byteLength === bytes.buffer.byteLength ? bytes.buffer : bytes.slice().buffer;
    });
}

function preload(slug, opts) {
    opts = opts || {};
    currentSlug = slug || currentSlug;
    if (ready) return ready;
    var t0 = performance.now();
    ready = support({ allowWasm: !!opts.allowWasm }).then(function (sup) {
        if (!sup.ok) throw typed('vc_unsupported_' + sup.reason, 'voice conversion cannot run here: ' + sup.reason);
        var plain = { url: window.__ortBuild.url(), wasmBase: window.__ortBuild.wasmBase(),
                      fallbackUrl: window.__ortBuild.fallback(), fallbackWasmBase: window.__ortBuild.wasmBase() };
        return fetchPack([meta('vc-cv-url'), meta('vc-cv-url-fallback')].filter(Boolean), opts.signal,
            opts.onProgress && function (p) { opts.onProgress(p, 'model'); }, slug).then(function (cvBuf) {
            if (opts.onProgress) opts.onProgress(1, 'compile');
            var rungs = sup.providers.slice();
            var step = function (i) {
                var prov = rungs[i], last = i === rungs.length - 1;
                var build = prov === 'webgpu' && sup.build ? sup.build : plain;
                return startRung(prov, build, cvBuf, last, slug).catch(function (e) {
                    if (last || (opts.signal && opts.signal.aborted)) throw e;
                    tele(slug, 'vc_rung_fallback', { from: prov, to: rungs[i + 1], code: e && e.code || null, msg: String(e && e.message || e).slice(0, 60) });
                    return step(i + 1);
                });
            };
            return step(0);
        });
    }).then(function (info) {
        readyInfo = info;
        tele(slug, 'vc_preload_ok', { ms: Math.round(performance.now() - t0), ep: info.provider, cv_ms: info.katMs.cv || 0 });
        return info;
    }).catch(function (e) {
        tele(slug, 'vc_preload_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
    return ready;
}

function ensureVoice(v, signal, onProgress, slug) {
    if (voicesWarm.indexOf(v.id) >= 0) { voicesWarm.splice(voicesWarm.indexOf(v.id), 1); voicesWarm.push(v.id); return Promise.resolve(); }
    if (voiceLoads[v.id]) return voiceLoads[v.id];
    var w = worker;
    voiceLoads[v.id] = fetchPack([v.url, v.fallbackUrl].filter(Boolean), signal, onProgress, slug).then(function (buf) {
        if (worker !== w || !w) throw typed('released', 'engine released');
        var p = waitFor('voice:' + v.id);
        w.postMessage({ type: 'voice', id: v.id, pack: buf }, [buf]);
        return p;
    }).then(function (m) {
        delete voiceLoads[v.id];
        readyInfo.katMs.gen = m.verdict && m.verdict.ms;
        voicesWarm.push(v.id);
        while (voicesWarm.length > MAX_VOICES_WARM) w.postMessage({ type: 'drop', id: voicesWarm.shift() });
    }, function (e) {
        delete voiceLoads[v.id];
        throw e;
    });
    return voiceLoads[v.id];
}

/** Mono 16 kHz — ContentVec's and RMVPE's passport. OfflineAudioContext downmixes (L+R)/2 and resamples. */
function to16kMono(audioBuffer) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var n = Math.max(1, Math.round(audioBuffer.duration * SR));
    var ctx = new Ctor(1, n, SR);
    var node = ctx.createBufferSource();
    node.buffer = audioBuffer; node.connect(ctx.destination); node.start();
    return ctx.startRendering().then(function (b) { return new Float32Array(b.getChannelData(0)); });
}
/**
 * The pitch of a recording depends on the recording only — the voice and the shift are applied
 * after it. People try several voices on one take; each try re-tracked it until 18.10.
 * Keyed by the prepared signal itself (length + 256 samples spread over it), last 3 kept.
 */
function fingerprint(x) {
    var h = 2166136261 ^ x.length, step = Math.max(1, Math.floor(x.length / 256));
    for (var i = 0; i < x.length; i += step) h = Math.imul(h ^ ((x[i] * 1e6) | 0), 16777619) >>> 0;
    return x.length + ':' + h;
}
var pitchCache = (function () {
    var keys = [], vals = {};
    return {
        get: function (k) { return vals[k] || null; },
        put: function (k, v) { if (!vals[k]) keys.push(k); vals[k] = v; while (keys.length > 3) delete vals[keys.shift()]; }
    };
})();

function bufferOf(pcm, sr) {
    var b = new AudioBuffer({ length: pcm.length, numberOfChannels: 1, sampleRate: sr });
    b.copyToChannel(pcm, 0);
    return b;
}

/**
 * Seconds of conversion work for `seconds` of audio on this device, from its own warm
 * known-answer runs (1 s of audio each, timed on the second run). Work ÷ (seconds × KAT ms),
 * parity runs 18.10.2026 (7 s and 38 s): WebGPU 0.46 (a 1-s call is mostly overhead on a card),
 * WASM 1.19 (pure compute; the windows' context adds the rest). Pitch (RmvpePitch) not included.
 * Refine from vc_convert_ok (work_ms against kat_ms).
 */
var WORK_K = { webgpu: 0.5, wasm: 1.2 };
// Pitch per second of audio when RmvpePitch has not timed this device yet — e2e stand 18.10,
// laptop: WebGPU ~0.1 s/s once warm, one CPU thread 1.6 s/s (14.4 s for 9.1 s).
var PITCH_S_PER_S = { webgpu: 0.1, wasm: 1.6 };
/**
 * Seconds of work for `seconds` of audio on this device; `withPitch` adds tracking the pitch
 * (skipped when this very recording was tracked before — pass false then).
 */
function estimateWorkS(seconds, withPitch) {
    var k = readyInfo && readyInfo.katMs;
    if (!k || !(k.cv > 0) || !(k.gen > 0) || !(seconds > 0)) return null;
    var w = (WORK_K[readyInfo.provider] || 1.2) * seconds * (k.cv + k.gen) / 1000;
    if (withPitch) {
        var rp = window.RmvpePitch, ri = rp && rp.info && rp.info();
        var p = ri && ri.chunkMs && rp.estimateWorkS ? rp.estimateWorkS(seconds + 2 * PAD_S_EST, ri.chunkMs) : null;
        w += p != null ? p : (PITCH_S_PER_S[readyInfo.provider] || 1.6) * seconds;
    }
    return w;
}
var PAD_S_EST = 1;                   // the 1-s reflect pads are tracked too

function convert(audioBuffer, opts) {
    opts = opts || {};
    var slug = opts.slug, signal = opts.signal || null, onProgress = opts.onProgress || null;
    var t0 = performance.now(), f0Ms = 0, xp = null, semis = 0, sourceF0 = 0;
    if (!opts.voice || !opts.voice.id || !opts.voice.url) return Promise.reject(typed('no_voice', 'voice is required'));
    var say = function (p, phase) { if (onProgress) try { onProgress(p, phase); } catch (_) {} };
    var job = ++seq;
    var stop = function () { if (worker) worker.postMessage({ type: 'cancel', job: job }); };
    if (signal) signal.addEventListener('abort', stop, { once: true });
    // Progress of three things that run AT ONCE — the conversion networks, this voice, the pitch —
    // then the conversion itself. The first run on a laptop GPU spent 9.8 s of its 24 waiting
    // for RMVPE behind the other two (e2e stand, 18.10).
    var part = { model: 0, voice: 0, pitch: 0 }, lastPhase = 'model';
    var setPart = function (k, p) { part[k] = Math.max(part[k], Math.min(1, p || 0)); lastPhase = k; say(0.25 * part.model + 0.1 * part.voice + 0.15 * part.pitch, lastPhase); };
    var hit = null;
    return to16kMono(audioBuffer).then(function (pcm) {
        var sh = S();
        xp = sh.reflectPad(sh.peakLimit(sh.highpass(pcm), 0.95), SR * sh.PAD_S);
        var key = fingerprint(xp);
        hit = pitchCache.get(key);
        var tf = performance.now();
        var pitchP = hit ? Promise.resolve(hit) : window.RmvpePitch.track(bufferOf(xp, SR), {
            slug: slug, signal: signal, allowWasm: !!opts.allowWasm,
            onProgress: function (p) { setPart('pitch', p); }
        }).then(function (r) {
            f0Ms = Math.round(performance.now() - tf);
            pitchCache.put(key, r);
            // the tracker's 91 MB stay warm only where memory is not tight: another voice on the
            // same recording reuses the pitch above, a new recording reloads it from the cache
            var mb = window.__memBudget;
            if (mb && ((mb.constrained && mb.constrained()) || (mb.gb && mb.gb() <= 4))) { try { window.RmvpePitch.release(); } catch (_) {} }
            return r;
        });
        var vcP = preload(slug, { signal: signal, allowWasm: !!opts.allowWasm, onProgress: function (p) { setPart('model', p); } }).then(function () {
            return ensureVoice(opts.voice, signal, function (p) { setPart('voice', p); }, slug);
        });
        return Promise.all([pitchP, vcP]).then(function (r) { return r[0]; });
    }).then(function (pitch) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var sh = S(), frames = Math.floor(xp.length / sh.HOP);
        // 'auto' — where the server lane would take this voice (VcShared.autoSemitones), from the
        // pitch RMVPE has just measured, not the page's own estimate
        sourceF0 = sh.voicedMedian(pitch.f0, pitch.conf);
        semis = opts.semitones === 'auto' ? sh.autoSemitones(sourceF0, opts.targetF0) : (+opts.semitones || 0);
        var f0 = sh.prepareF0(pitch.f0, pitch.conf, semis, frames);
        var core = readyInfo.provider === 'webgpu' ? 2000 : 1000;
        var p = waitFor('job:' + job, { onProgress: function (done, of) { say(0.5 + 0.5 * done / of, 'convert'); } });
        worker.postMessage({ type: 'convert', job: job, voice: opts.voice.id, pcm: xp, f0: f0,
                             seed: opts.seed || 1, core: core, ctx: 100 }, [xp.buffer]);
        return p;
    }).then(function (m) {
        if (signal) signal.removeEventListener('abort', stop);
        tele(slug, 'vc_convert_ok', { sec: Math.round(audioBuffer.duration), ms: Math.round(performance.now() - t0),
                                      work_ms: m.ms, f0_ms: f0Ms, f0_cached: hit ? 1 : 0, ep: readyInfo && readyInfo.provider, voice: opts.voice.id,
                                      semi: semis, semi_auto: opts.semitones === 'auto' ? 1 : 0, src_f0: Math.round(sourceF0),
                                      kat_cv: readyInfo && readyInfo.katMs.cv || null, kat_gen: readyInfo && readyInfo.katMs.gen || null });
        return { audio: m.audio, sr: m.sr, provider: readyInfo && readyInfo.provider, ms: m.ms, f0Ms: f0Ms,
                 semitones: semis, sourceF0: sourceF0 };
    }).catch(function (e) {
        if (signal) signal.removeEventListener('abort', stop);
        if ((signal && signal.aborted) || (e && (e.name === 'AbortError' || e.code === 'cancelled' || e.code === 'released'))) throw new DOMException('aborted', 'AbortError');
        tele(slug, 'vc_convert_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80), ep: readyInfo && readyInfo.provider });
        if (e && (e.code === 'gpu_output_corrupt' || e.code === 'kat_after_failed')) {
            if (e.code === 'kat_after_failed' && cacheKey && window.OrtEpVerify) { try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat_after' }); } catch (_) {} }
            release();
        }
        throw e;
    });
}

window.VoiceConvert = {
    support: support,
    preload: preload,
    convert: convert,
    estimateWorkS: estimateWorkS,
    release: release,
    isWarm: function () { return !!readyInfo; },
    info: function () { return readyInfo; }
};
})();
